import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { 
    ArrowLeft, 
    Save, 
    Loader2,
    Bold,
    Italic,
    Heading1,
    Heading2,
    List,
    ListOrdered,
    Link as LinkIcon,
    Image as ImageIcon,
    Quote,
    Code
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
    { slug: 'seo', name: 'SEO' },
    { slug: 'gmb-local-seo', name: 'GMB / Local SEO' },
    { slug: 'ai-search', name: 'AI Search' },
    { slug: 'case-studies', name: 'Case Studies' },
    { slug: 'guides', name: 'Guides' },
];

export default function BlogEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();
    const editorRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        category: 'seo',
        tags: '',
        author: 'Aitrixy Team',
        featured_image: '',
        meta_title: '',
        meta_description: '',
        is_published: false,
    });

    const isEditing = !!id;

    useEffect(() => {
        if (isEditing) {
            fetchPost();
        }
    }, [id]);

    const fetchPost = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/blog/all`, getAuthHeader());
            const post = response.data.find(p => p.id === id);
            if (post) {
                setFormData({
                    title: post.title || '',
                    slug: post.slug || '',
                    excerpt: post.excerpt || '',
                    content: post.content || '',
                    category: post.category || 'seo',
                    tags: post.tags?.join(', ') || '',
                    author: post.author || 'Aitrixy Team',
                    featured_image: post.featured_image || '',
                    meta_title: post.meta_title || '',
                    meta_description: post.meta_description || '',
                    is_published: post.is_published || false,
                });
            }
        } catch (error) {
            console.error('Error fetching post:', error);
            toast.error('Failed to load post');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Auto-generate slug from title
        if (name === 'title' && !isEditing) {
            const slug = value
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/(^-|-$)/g, '');
            setFormData(prev => ({ ...prev, slug }));
        }
    };

    const insertFormat = (format) => {
        const textarea = editorRef.current;
        if (!textarea) return;
        
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = formData.content.substring(start, end);
        
        let newText = '';
        let cursorOffset = 0;
        
        switch (format) {
            case 'bold':
                newText = `<strong>${selectedText || 'bold text'}</strong>`;
                cursorOffset = selectedText ? newText.length : 8;
                break;
            case 'italic':
                newText = `<em>${selectedText || 'italic text'}</em>`;
                cursorOffset = selectedText ? newText.length : 4;
                break;
            case 'h1':
                newText = `<h1>${selectedText || 'Heading 1'}</h1>\n`;
                break;
            case 'h2':
                newText = `<h2>${selectedText || 'Heading 2'}</h2>\n`;
                break;
            case 'ul':
                newText = `<ul>\n    <li>${selectedText || 'List item'}</li>\n</ul>\n`;
                break;
            case 'ol':
                newText = `<ol>\n    <li>${selectedText || 'List item'}</li>\n</ol>\n`;
                break;
            case 'link':
                newText = `<a href="URL">${selectedText || 'Link text'}</a>`;
                break;
            case 'image':
                newText = `<img src="IMAGE_URL" alt="${selectedText || 'Image description'}" />`;
                break;
            case 'quote':
                newText = `<blockquote>${selectedText || 'Quote text'}</blockquote>\n`;
                break;
            case 'code':
                newText = `<code>${selectedText || 'code'}</code>`;
                break;
            default:
                return;
        }
        
        const newContent = formData.content.substring(0, start) + newText + formData.content.substring(end);
        setFormData(prev => ({ ...prev, content: newContent }));
        
        // Focus back on textarea
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + newText.length, start + newText.length);
        }, 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title || !formData.slug || !formData.content) {
            toast.error('Please fill in all required fields');
            return;
        }

        setSaving(true);
        
        const postData = {
            ...formData,
            tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        };

        try {
            if (isEditing) {
                await axios.put(`${API}/blog/${id}`, postData, getAuthHeader());
                toast.success('Post updated successfully');
            } else {
                await axios.post(`${API}/blog`, postData, getAuthHeader());
                toast.success('Post created successfully');
            }
            navigate('/admin/blog');
        } catch (error) {
            console.error('Error saving post:', error);
            toast.error(error.response?.data?.detail || 'Failed to save post');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div data-testid="blog-editor">
            <div className="flex items-center gap-4 mb-8">
                <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate('/admin/blog')}
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                </Button>
                <h1 className="text-3xl font-bold">
                    {isEditing ? 'Edit Post' : 'New Post'}
                </h1>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Title */}
                        <div className="space-y-2">
                            <Label htmlFor="title">Title *</Label>
                            <Input
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="Enter post title"
                                className="text-lg"
                                required
                                data-testid="post-title"
                            />
                        </div>

                        {/* Slug */}
                        <div className="space-y-2">
                            <Label htmlFor="slug">Slug *</Label>
                            <Input
                                id="slug"
                                name="slug"
                                value={formData.slug}
                                onChange={handleChange}
                                placeholder="post-url-slug"
                                required
                                data-testid="post-slug"
                            />
                        </div>

                        {/* Excerpt */}
                        <div className="space-y-2">
                            <Label htmlFor="excerpt">Excerpt *</Label>
                            <Textarea
                                id="excerpt"
                                name="excerpt"
                                value={formData.excerpt}
                                onChange={handleChange}
                                placeholder="Brief description of the post"
                                rows={2}
                                required
                                data-testid="post-excerpt"
                            />
                        </div>

                        {/* Content Editor */}
                        <div className="space-y-2">
                            <Label>Content *</Label>
                            
                            {/* Toolbar */}
                            <div className="flex flex-wrap gap-1 p-2 rounded-t-lg border border-b-0 border-input bg-muted/30">
                                <button
                                    type="button"
                                    onClick={() => insertFormat('bold')}
                                    className="p-2 rounded hover:bg-muted"
                                    title="Bold"
                                >
                                    <Bold className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => insertFormat('italic')}
                                    className="p-2 rounded hover:bg-muted"
                                    title="Italic"
                                >
                                    <Italic className="w-4 h-4" />
                                </button>
                                <div className="w-px bg-border mx-1" />
                                <button
                                    type="button"
                                    onClick={() => insertFormat('h1')}
                                    className="p-2 rounded hover:bg-muted"
                                    title="Heading 1"
                                >
                                    <Heading1 className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => insertFormat('h2')}
                                    className="p-2 rounded hover:bg-muted"
                                    title="Heading 2"
                                >
                                    <Heading2 className="w-4 h-4" />
                                </button>
                                <div className="w-px bg-border mx-1" />
                                <button
                                    type="button"
                                    onClick={() => insertFormat('ul')}
                                    className="p-2 rounded hover:bg-muted"
                                    title="Bullet List"
                                >
                                    <List className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => insertFormat('ol')}
                                    className="p-2 rounded hover:bg-muted"
                                    title="Numbered List"
                                >
                                    <ListOrdered className="w-4 h-4" />
                                </button>
                                <div className="w-px bg-border mx-1" />
                                <button
                                    type="button"
                                    onClick={() => insertFormat('link')}
                                    className="p-2 rounded hover:bg-muted"
                                    title="Link"
                                >
                                    <LinkIcon className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => insertFormat('image')}
                                    className="p-2 rounded hover:bg-muted"
                                    title="Image"
                                >
                                    <ImageIcon className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => insertFormat('quote')}
                                    className="p-2 rounded hover:bg-muted"
                                    title="Quote"
                                >
                                    <Quote className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => insertFormat('code')}
                                    className="p-2 rounded hover:bg-muted"
                                    title="Code"
                                >
                                    <Code className="w-4 h-4" />
                                </button>
                            </div>
                            
                            <Textarea
                                ref={editorRef}
                                id="content"
                                name="content"
                                value={formData.content}
                                onChange={handleChange}
                                placeholder="Write your post content here... (HTML supported)"
                                rows={20}
                                className="rounded-t-none font-mono text-sm"
                                required
                                data-testid="post-content"
                            />
                            <p className="text-xs text-muted-foreground">
                                HTML formatting is supported. Use the toolbar or write HTML directly.
                            </p>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Publish */}
                        <div className="p-6 rounded-2xl border border-border bg-background">
                            <h3 className="font-semibold mb-4">Publish</h3>
                            
                            <div className="flex items-center justify-between mb-4">
                                <Label htmlFor="is_published">Published</Label>
                                <Switch
                                    id="is_published"
                                    checked={formData.is_published}
                                    onCheckedChange={(checked) => 
                                        setFormData(prev => ({ ...prev, is_published: checked }))
                                    }
                                    data-testid="post-published"
                                />
                            </div>

                            <Button 
                                type="submit" 
                                className="w-full rounded-full"
                                disabled={saving}
                                data-testid="save-post-btn"
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 mr-2" />
                                        {isEditing ? 'Update Post' : 'Create Post'}
                                    </>
                                )}
                            </Button>
                        </div>

                        {/* Category & Tags */}
                        <div className="p-6 rounded-2xl border border-border bg-background space-y-4">
                            <h3 className="font-semibold">Categorization</h3>
                            
                            <div className="space-y-2">
                                <Label htmlFor="category">Category</Label>
                                <select
                                    id="category"
                                    name="category"
                                    value={formData.category}
                                    onChange={handleChange}
                                    className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                                    data-testid="post-category"
                                >
                                    {categories.map(cat => (
                                        <option key={cat.slug} value={cat.slug}>
                                            {cat.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="tags">Tags</Label>
                                <Input
                                    id="tags"
                                    name="tags"
                                    value={formData.tags}
                                    onChange={handleChange}
                                    placeholder="tag1, tag2, tag3"
                                    data-testid="post-tags"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Separate tags with commas
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="author">Author</Label>
                                <Input
                                    id="author"
                                    name="author"
                                    value={formData.author}
                                    onChange={handleChange}
                                    placeholder="Author name"
                                    data-testid="post-author"
                                />
                            </div>
                        </div>

                        {/* Featured Image */}
                        <div className="p-6 rounded-2xl border border-border bg-background space-y-4">
                            <h3 className="font-semibold">Featured Image</h3>
                            
                            <div className="space-y-2">
                                <Label htmlFor="featured_image">Image URL</Label>
                                <Input
                                    id="featured_image"
                                    name="featured_image"
                                    value={formData.featured_image}
                                    onChange={handleChange}
                                    placeholder="https://..."
                                    data-testid="post-image"
                                />
                            </div>

                            {formData.featured_image && (
                                <div className="aspect-video rounded-lg overflow-hidden bg-muted">
                                    <img 
                                        src={formData.featured_image} 
                                        alt="Preview"
                                        className="w-full h-full object-cover"
                                        onError={(e) => e.target.style.display = 'none'}
                                    />
                                </div>
                            )}
                        </div>

                        {/* SEO */}
                        <div className="p-6 rounded-2xl border border-border bg-background space-y-4">
                            <h3 className="font-semibold">SEO</h3>
                            
                            <div className="space-y-2">
                                <Label htmlFor="meta_title">Meta Title</Label>
                                <Input
                                    id="meta_title"
                                    name="meta_title"
                                    value={formData.meta_title}
                                    onChange={handleChange}
                                    placeholder="SEO title"
                                    data-testid="post-meta-title"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="meta_description">Meta Description</Label>
                                <Textarea
                                    id="meta_description"
                                    name="meta_description"
                                    value={formData.meta_description}
                                    onChange={handleChange}
                                    placeholder="SEO description"
                                    rows={3}
                                    data-testid="post-meta-desc"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
